import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import {
  Eye, MousePointerClick, MessageCircle, Users, TrendingUp,
  ShoppingBag, IndianRupee, ArrowDown,
} from "lucide-react";

const COLORS = ["#FF6B57", "#FF8EA4", "#F59E0B", "#3B82F6", "#14B8A6", "#10B981"];
const FUNNEL_COLORS = ["#3B82F6", "#6366F1", "#8B5CF6", "#F59E0B", "#10B981"];

export function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/admin/analytics?days=${days}`);
        setData(d);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Analytics fetch failed:", err.message);
        }
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [days]);

  if (loading) return <div className="py-12 text-center text-slate-500 font-body">Loading analytics...</div>;
  if (!data) return <div className="py-12 text-center text-slate-500 font-body">Failed to load analytics</div>;

  const metrics = [
    { label: "Total Visits", value: data.total_visits, icon: Eye, testId: "analytics-visits-metric", color: "text-coral" },
    { label: "Unique Visitors", value: data.unique_visitors, icon: Users, color: "text-blue-500" },
    { label: "Impressions", value: data.total_impressions, icon: TrendingUp, color: "text-teal-500" },
    { label: "Detail Taps", value: data.total_detail_taps, icon: MousePointerClick, color: "text-amber-500" },
    { label: "DM/Buy Clicks", value: data.total_dm_clicks, icon: MessageCircle, testId: "analytics-dm-clicks-metric", color: "text-pink-500" },
  ];

  const funnel = data.funnel || [];
  const funnelMax = funnel.length > 0 ? funnel[0].count : 1;

  const totalRevenue = (data.sales_by_product || []).reduce((s, p) => s + (p.revenue || 0), 0);
  const totalSales = (data.sales_by_product || []).reduce((s, p) => s + (p.sales || 0), 0);

  return (
    <div className="space-y-4 mt-4">
      {/* Date Range Selector */}
      <div className="flex gap-2 justify-end">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
              days === d ? "bg-coral text-white" : "bg-white text-slate-500 border border-border"
            }`}>
            {d}d
          </button>
        ))}
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="bg-white rounded-2xl p-4" data-testid={m.testId}>
            <m.icon className={`w-5 h-5 ${m.color} mb-2`} />
            <p className="text-2xl font-heading font-bold text-slate-800">{m.value}</p>
            <p className="text-xs text-slate-500 font-body">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Conversion Funnel */}
      {funnel.length > 0 && funnelMax > 0 && (
        <div className="bg-white rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm text-slate-700 mb-4">Conversion Funnel</h3>
          <div className="space-y-2">
            {funnel.map((step, i) => {
              const pct = funnelMax > 0 ? ((step.count / funnelMax) * 100) : 0;
              const prevCount = i > 0 ? funnel[i - 1].count : step.count;
              const dropPct = prevCount > 0 ? (((prevCount - step.count) / prevCount) * 100) : 0;
              return (
                <div key={step.step}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-600">{step.step}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-800">{step.count}</span>
                      {i > 0 && dropPct > 0 && (
                        <span className="text-[10px] text-red-400 flex items-center">
                          <ArrowDown className="w-2.5 h-2.5" />{dropPct.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(pct, 2)}%`,
                        backgroundColor: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sales & Revenue */}
      {(data.sales_by_product?.length > 0 || totalSales > 0) && (
        <div className="bg-white rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm text-slate-700 mb-3">Sales & Revenue</h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <ShoppingBag className="w-5 h-5 text-green-600 mx-auto mb-1" />
              <p className="text-xl font-heading font-bold text-green-700">{totalSales}</p>
              <p className="text-[10px] text-green-600">Total Sales</p>
            </div>
            <div className="bg-coral-light rounded-xl p-3 text-center">
              <IndianRupee className="w-5 h-5 text-coral mx-auto mb-1" />
              <p className="text-xl font-heading font-bold text-coral">₹{(totalRevenue / 100).toLocaleString()}</p>
              <p className="text-[10px] text-coral/70">Total Revenue</p>
            </div>
          </div>
          {data.sales_by_product?.length > 0 && (
            <div className="space-y-2">
              {data.sales_by_product.map((p) => (
                <div key={p._id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <span className="text-xs font-medium text-slate-700 truncate flex-1">{p.title}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-slate-500">{p.sales} sold</span>
                    <span className="text-xs font-bold text-slate-800">₹{(p.revenue / 100).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Visits Over Time */}
      {data.visits_over_time?.length > 0 && (
        <div className="bg-white rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm text-slate-700 mb-3">Visits Over Time</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.visits_over_time}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1E7D0" />
              <XAxis dataKey="_id" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#FF6B57" strokeWidth={2} dot={{ fill: "#FF6B57", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Packs by Impressions */}
      {data.top_impressions?.length > 0 && (
        <div className="bg-white rounded-2xl p-4">
          <h3 className="font-heading font-bold text-sm text-slate-700 mb-3">Top Packs by Views</h3>
          <ResponsiveContainer width="100%" height={Math.max(120, data.top_impressions.length * 32)}>
            <BarChart data={data.top_impressions} layout="vertical" margin={{ left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1E7D0" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis type="category" dataKey="title" tick={{ fontSize: 10, fill: "#64748B" }} width={90} />
              <Tooltip />
              <Bar dataKey="count" fill="#FF6B57" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Source Breakdown */}
      {data.source_breakdown?.length > 0 && (
        <div className="bg-white rounded-2xl p-4" data-testid="analytics-attribution-chart">
          <h3 className="font-heading font-bold text-sm text-slate-700 mb-3">Source Attribution</h3>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.source_breakdown} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                  {data.source_breakdown.map((entry, i) => (
                    <Cell key={entry._id || `source-${i}`} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 text-sm shrink-0">
              {data.source_breakdown.map((s, i) => (
                <div key={s._id} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-slate-600 font-body">{s._id}: {s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {data.total_visits === 0 && (
        <div className="text-center py-8 text-slate-500 font-body">
          <p>No analytics data yet.</p>
          <p className="text-sm mt-1">Share your catalog link to start collecting insights!</p>
        </div>
      )}
    </div>
  );
}
